<template>
    <div
        id="app"
        :class="appClassList"
        class="payment-frame-app"
    >
        <div
            :class="mainWrapperClassList"
            class="main-wrapper"
        >
            <MLoader v-if="isLoading" />
            <div v-show="isShowCardForm">
                <PCard
                    v-if="isCardForm"
                    :token="token"
                    :payment="payment"
                    :sci-platform="sciPlatform"
                    :is-disabled="isDisabled"
                    @changeIsDisabled="handleIsDisabled"
                />
            </div>
            <AGlobalNotifications />
        </div>
    </div>
</template>

<script>
import PCard from './components/pages/p-card';
import MLoader from './components/molecules/m-loader';
import AGlobalNotifications from './components/atoms/a-global-notifications';

import EventBus from './utils/event-bus';
import {
    getToken,
    getUserIp
} from './utils/services/user-service';
import {
    isJavaEnabled,
    isMobileBrowser,
    getUserAgentInfo,
    getTimezoneOffset,
    getBrowserLanguage,
    getScreenColorDepth,
    getWindowSizeParams,
    // sendMessageToParentWindow
} from './utils/services/window-service';

import {
    goToUrl,
    getTokenFromUrl
} from './utils/services/url-service';
import { socket } from './utils/services/websocket-service';
import { CONFIG } from './utils/services/global-config-service';
import { notifyError } from './utils/services/notification-service';
import {
    UPDATE_STATUS,
    GET_FINAL_STATUS,
    CHANGE_IS_SHOW_LOADER
} from './utils/constants/event-constants';
import {
    SCI_SIDEBAR_SIZE,
    SCREEN_SIZE_MOBILE,
    SCREEN_SIZE_DESKTOP,
    CSS_SIZES_VARIABLES_LIST,
} from './utils/constants/screen-size-constants';
import {
    PLATFORM_AM,
    PLATFORM_AC,
    THEME_MODES,
    THEME_COLORS,
    THEME_SIZE_POINTS
} from './utils/constants/theme-config-constants';
import { DEVICE } from './utils/constants/app-constants';
import { CARD_STATUS } from './utils/constants/status-constants';
import { SOCKET_CONSTANTS } from './utils/constants/websocket-constats';
import { PAYMENT_TYPES } from './utils/constants/payment-type-constatnts';

export default {
    name: 'App',
    components: {
        PCard,
        MLoader,
        AGlobalNotifications
    },
    data() {
        return {
            clientIp: null,
            payment: {
                status: '',
                data: {},
                payway: '',
                on_submit: ''
            },
            isPendingRequest: false,
            isShowLoader: false,
            token: getToken(),
            isDisabled: false,
            sciPlatform: PLATFORM_AM,
            color: THEME_COLORS.BLACK,
            mode: THEME_MODES.DAY,
            applySizesInPoints: THEME_SIZE_POINTS.PX,
            isMobile: false
        }
    },
    created() {
        this.setDefaultThemeConfig();
    },
    mounted() {
        this.setThemeSizesInPoints();
        this.setClientIp();
        this.initListeners();
    },
    beforeDestroy() {
        this.deactivateListeners();
    },
    computed: {
        isHiddenForm() {
            return this.payment.data && this.payment.data.is_hidden;
        },
        isLoading() {
            return this.isPendingRequest || this.isShowLoader || this.isHiddenForm;
        },
        isApplySizesInVw() {
            return this.applySizesInPoints === THEME_SIZE_POINTS.VW;
        },
        themeColor: {
            get() {
                return this.color;
            },
            set(color) {
                if (this.isThemeColorExist(color)) {
                    this.color = color;
                }
            }
        },
        themeMode: {
            get() {
                return this.mode;
            },
            set(mode) {
                if (this.isThemeModeExist(mode)) {
                    this.mode = mode;
                }
            }
        },
        themeSizePoints: {
            get() {
                return this.applySizesInPoints;
            },
            set(mode) {
                if (this.isSizePointExist(mode)) {
                    this.applySizesInPoints = mode;
                }
            }
        },
        appClassList() {
            return [
                `theme-${this.themeColor}`,
                `theme--${this.themeMode}`,
                `theme-sizes-in--${this.themeSizePoints}`
            ];
        },
        mainWrapperClassList() {
            const classList = [`platform--${this.sciPlatform}`];
            if (this.isMobile) {
                classList.push('mobile-screen');
            }
            return classList;
        },
        paymentPayway() {
            return this.payment.payway || '';
        },
        isCardForm() {
            return this.paymentPayway === PAYMENT_TYPES.CARD;
        },
        isShowCardForm() {
            return this.isCardForm && !this.isHiddenForm;
        }
    },
    watch: {
        payment: {
            handler(value) {
                EventBus.$emit(UPDATE_STATUS, value.status);

                if ([CARD_STATUS.SUCCESS, CARD_STATUS.FAIL].includes(value.status)) {
                    EventBus.$emit(GET_FINAL_STATUS, value.status);
                }

                this.paymentStatusRedirectHandler();
            },
            immediate: true,
            deep: true
        }
    },
    methods: {
        initListeners() {
            socket.on(SOCKET_CONSTANTS.SERVER_SEND_FORM_ERROR, this.onFormError);
            socket.on(SOCKET_CONSTANTS.PAYMENT_CHANGED_STATUS, this.handlePayment);
            EventBus.$on(CHANGE_IS_SHOW_LOADER, this.handleIsShowLoader);
            // TODO: move window event listener to separate SERVICE
            window.addEventListener('message', this.handleWindowParentMessage);
            window.addEventListener('load', this.sendInitialDataToServer, { once: true });
        },
        deactivateListeners() {
            EventBus.$off(CHANGE_IS_SHOW_LOADER, this.handleIsShowLoader);
            // TODO: move window event listener to separate SERVICE
            window.removeEventListener('message', this.handleWindowParentMessage);
        },
        async setClientIp() {
            try {
                this.isPendingRequest = true;
                this.clientIp = await getUserIp();
            } catch (e) {
                notifyError(e);
            } finally {
                this.isPendingRequest = false;
            }
        },
        onFormError(response) {
            this.isDisabled = false;
            Object.keys(response.error).forEach(errorKey => notifyError(errorKey));
        },
        handlePayment(paymentData) {
            if (paymentData.status) {
                this.payment = paymentData;
            } else {
                notifyError();
            }
        },
        handleIsShowLoader(isShow) {
            this.isShowLoader = isShow;
        },
        handleIsDisabled(isDisabled) {
            this.isDisabled = isDisabled;
        },
        handleWindowParentMessage(event) {
            const config = event && event.data;
            if (config) {
                if (config.color) this.themeColor = config.color;
                if (config.mode) this.themeMode = config.mode;
            }
        },
        isThemeColorExist(color) {
            return Object.values(THEME_COLORS).includes(color);
        },
        isThemeModeExist(mode) {
            return Object.values(THEME_MODES).includes(mode);
        },
        isSizePointExist(sizePoint) {
            return Object.values(THEME_SIZE_POINTS).includes(sizePoint);
        },
        setDefaultThemeConfig() {
            if (CONFIG.COLOR) this.themeColor = CONFIG.COLOR;
            if (CONFIG.MODE) this.themeMode = CONFIG.MODE;
            if (CONFIG.SCI_PLATFORM?.toLowerCase() !== PLATFORM_AM) this.sciPlatform = PLATFORM_AC;
            if (CONFIG.APPLY_SIZES_IN_POINTS) this.themeSizePoints = CONFIG.APPLY_SIZES_IN_POINTS;
            if (CONFIG.IS_MOBILE) this.isMobile = true;
        },
        vwSizes(currScreenSize = SCREEN_SIZE_DESKTOP - SCI_SIDEBAR_SIZE) {
            CSS_SIZES_VARIABLES_LIST.forEach(size => {
                const name = `--size-${size}`;
                const value = `${(size * 100) / currScreenSize}vw`;
                document.documentElement.style.setProperty(name, value);
            });
        },
        setThemeSizesInPoints() {
            if (!this.isApplySizesInVw) return;
            this.isMobile
                ? this.vwSizes(SCREEN_SIZE_MOBILE)
                : this.vwSizes();
        },
        sendInitialDataToServer() {
            const {
                outerWidth,
                outerHeight,
                innerHeight,
                innerWidth
            } = getWindowSizeParams();

            const initialData = {
                token: getTokenFromUrl(),
                browserInfo: {
                    browser_java_enabled: isJavaEnabled(),
                    browser_language: getBrowserLanguage(),
                    browser_user_agent: getUserAgentInfo(),
                    browser_color_depth: String(getScreenColorDepth()),
                    browser_tz: String(getTimezoneOffset()),
                    browser_screen_width: String(outerWidth),
                    browser_screen_height: String(outerHeight),
                    window_height: String(innerHeight),
                    window_width: String(innerWidth),
                    device_channel: isMobileBrowser() ? DEVICE.MOBILE : DEVICE.DESKTOP
                }
            };

            socket.emit(SOCKET_CONSTANTS.SET_INITIAL_DATA, initialData);
        },
        paymentStatusRedirectHandler() {
            const isSuccessExist = this.payment.status === CARD_STATUS.SUCCESS && Boolean(this.payment.success_url);
            const isFailureExist = this.payment.status === CARD_STATUS.FAIL && Boolean(this.payment.failure_url);

            if (isSuccessExist) {
                goToUrl(this.payment.success_url);
            } else if (isFailureExist) {
                goToUrl(this.payment.failure_url);
            }
        }
    }
}
</script>
